<template>
  <v-menu ref="menu" v-model="datePicker" :close-on-content-click="false" max-width="290" transition="scale-transition" offset-y>
    <template v-slot:activator="{ on, attrs }">
      <v-text-field v-model="range" v-bind="attrs" v-on="on" class="mr-1" :class="['input-lg']" solo dense hide-details readonly>
        <template #append>
          <v-icon class="icon" :class="{ 'primary--text': datePicker }" @click="on.click">mdi-calendar</v-icon>
        </template>
      </v-text-field>
    </template>
    <!-- eslint-disable-next-line prettier/prettier -->
    <v-date-picker
      v-model="dates" ref="datePicker" :max="max" color="primary" range no-title scrollable
      :day-format="date => new Date(date).getDate()"
      @change="change" @click:date="pickQuick" @click:month="pickQuick"  @click:year="pickQuick"
    >
      <!--
        Commented.by.KWShin.2024.09.05.
        QuickPick 기능은 DatePicker 하단에 '일/주/월/분기/년' 버튼이 나타나서
        버튼을 클릭하면 DatePicker의 화면이 해당 기간을 선택할 수 있는 화면으로 바뀌고
        일/월/년을 선택하면 해당 일이 포함된 일이나 주/해당 월이 포함된 월이나 분기/해당 년의
        기간이 자동적으로 선택되도록 하는 기능이다.
      -->
      <v-card>
        <v-btn-toggle v-if="!disabledQuickPick" v-model="mode" class="d-flex" dense>
          <v-btn small @click="setType('DATE')">{{ $t('components.dtrp.day') }}</v-btn>
          <v-btn small @click="setType('DATE')">{{ $t('components.dtrp.week') }}</v-btn>
          <v-btn small @click="setType('MONTH')">{{ $t('components.dtrp.month') }}</v-btn>
          <v-btn small @click="setType('MONTH')">{{ $t('components.dtrp.quarter') }}</v-btn>
          <v-btn small @click="setType('YEAR')">{{ $t('components.dtrp.year') }}</v-btn>
        </v-btn-toggle>
        <v-menu v-model="bgnTimePicker" :close-on-content-click="false" transition="scale-transition" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <!-- eslint-disable-next-line prettier/prettier -->
            <v-text-field v-model="begin" v-bind="attrs" v-on="on"
              append-icon="mdi-clock-time-four-outline" @click:append="on.click"
              class="mr-1" :class="['input-xlg']" solo dense hide-details readonly
            >
              <template #prepend>
                <div class="print-section">{{ `${$t('cmpnt.begin.dt')} : ` }}</div>
              </template>
            </v-text-field>
          </template>
          <!-- eslint-disable-next-line prettier/prettier -->
          <v-time-picker :value="times[0]" @change="setBeginTime"
            @input="updateBeginTime"
            format="ampm" use-seconds scrollable
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="bgnTimePicker = false">
              {{ $t('common.select.done') }}
            </v-btn>
          </v-time-picker>
        </v-menu>
        <v-menu v-model="endTimePicker" :close-on-content-click="false" transition="scale-transition" offset-y>
          <template v-slot:activator="{ on, attrs }">
            <!-- eslint-disable-next-line prettier/prettier -->
            <v-text-field v-model="end" v-bind="attrs" v-on="on"
              append-icon="mdi-clock-time-four-outline" @click:append="on.click"
              class="mr-1" :class="['input-xlg']" solo dense hide-details readonly
            >
              <template #prepend>
                <div class="print-section">{{ `${$t('cmpnt.end.dt')} : ` }}</div>
              </template>
            </v-text-field>
          </template>
          <!-- eslint-disable-next-line prettier/prettier -->
          <v-time-picker :value="times[1]" @change="setEndTime"
            @input="updateEndTime"
            format="ampm" use-seconds scrollable
          >
            <v-spacer></v-spacer>
            <v-btn text color="primary" @click="endTimePicker = false">
              {{ $t('common.select.done') }}
            </v-btn>
          </v-time-picker>
        </v-menu>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="datePicker = false">
            {{ $t('common.select.done') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-date-picker>
  </v-menu>
</template>

<script>
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import isEqual from 'date-fns/isEqual';
import isBefore from 'date-fns/isBefore';
import addHours from 'date-fns/addHours';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import startOfWeek from 'date-fns/startOfWeek';
import lastDayOfWeek from 'date-fns/lastDayOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import startOfQuarter from 'date-fns/startOfQuarter';
import endOfQuarter from 'date-fns/endOfQuarter';
import startOfYear from 'date-fns/startOfYear';
import endOfYear from 'date-fns/endOfYear';

const DAY_MODE = 0;
const WEEK_MODE = 1;
const MONTH_MODE = 2;
const QUARTER_MODE = 3;
const YEAR_MODE = 4;
/*
 * max 값 계산해서 디스플레이 및 emit 등에 적용
 */
export default {
  props: {
    value: {
      type: Array,
      default: () => {
        return [startOfDay(new Date()), endOfDay(new Date())];
      },
    },
    disabledQuickPick: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      datePicker: false,
      bgnTimePicker: false,
      endTimePicker: false,
      mode: null,
      max: format(new Date(), 'yyyy-MM-dd'),
      hours: this.getTimeUnitValue(24),
      minutes: this.getTimeUnitValue(60),
      seconds: this.getTimeUnitValue(60),
      dates: this.getDates(),
      times: this.getTimes(),
      pattern: 'yyyy-MM-dd HH:mm:ss',
      origin: [],
    };
  },
  computed: {
    range() {
      const range = [];
      if (this.getBeginDateTime() !== null) {
        range.splice(0, 1, format(this.getBeginDateTime(), this.pattern));
      }
      if (this.getEndDateTime() !== null) {
        range.splice(1, 1, format(this.getEndDateTime(), this.pattern));
      }
      return range.join(' ~ ');
    },
    begin() {
      return this.getBeginDateTime() !== null ? format(this.getBeginDateTime(), this.pattern) : '---';
    },
    end() {
      return this.getEndDateTime() !== null ? format(this.getEndDateTime(), this.pattern) : '---';
    },
  },
  watch: {
    value() {
      this.dates = this.getDates();
      this.times = this.getTimes();
    },
    datePicker(value) {
      if (!value) {
        if (this.getBeginDateTime() === null && this.getEndDateTime() === null) {
          this.emit(null);
        } else if (this.getEndDateTime() === null || isBefore(this.getEndDateTime(), this.getBeginDateTime())) {
          this.setEndDateTime(addHours(this.getBeginDateTime(), 1));
          this.emit();
        } else if (!isEqual(this.origin[0], this.getBeginDateTime()) || !isEqual(this.origin[1], this.getEndDateTime())) {
          this.emit();
        }
      }
    },
  },
  created() {
    this.origin = [this.getBeginDateTime(), this.getEndDateTime()];
  },
  methods: {
    getBeginDateTime() {
      return this.getDateTime(0);
    },
    setBeginDateTime(datetime) {
      this.setDateTime(0, datetime);
    },
    getEndDateTime() {
      return this.getDateTime(1);
    },
    setEndDateTime(datetime) {
      this.setDateTime(1, datetime);
    },
    getDateTime(index) {
      let date = this.dates[index];
      return date !== undefined ? parseISO(date + 'T' + this.times[index]) : null;
    },
    setDateTime(index, datetime) {
      this.dates.splice(index, 1, format(datetime, 'yyyy-MM-dd'));
      this.times.splice(index, 1, format(datetime, 'HH:mm:ss'));
    },
    change() {
      if (isBefore(this.getEndDateTime(), this.getBeginDateTime())) {
        this.dates.reverse();
      }
    },
    emit(value = [this.getBeginDateTime(), this.getEndDateTime()]) {
      this.$emit('input', value);
      this.$emit('change', value);
    },
    getTimeUnitValue(size) {
      const result = [];
      for (let i = 0; i < size; i++) {
        result.push(i.toString().padStart(2, '0'));
      }
      return result;
    },
    setType(type) {
      this.$refs.datePicker.activePicker = type;
    },
    pickQuick(value) {
      if (this.mode != null) {
        const date = parseISO(this.mode === YEAR_MODE ? value + '-01-01' : value);
        let dates;
        if (this.mode === DAY_MODE) {
          dates = [date, date];
        }
        if (this.mode === WEEK_MODE) {
          dates = [startOfWeek(date), lastDayOfWeek(date)];
        }
        if (this.mode === MONTH_MODE) {
          dates = [startOfMonth(date), endOfMonth(date)];
        }
        if (this.mode === QUARTER_MODE) {
          dates = [startOfQuarter(date), endOfQuarter(date)];
        }
        if (this.mode === YEAR_MODE) {
          dates = [startOfYear(date), endOfYear(date)];
        }
        this.setBeginDateTime(startOfDay(dates[0]));
        this.setEndDateTime(endOfDay(dates[1]));
        this.mode = null;
        this.setType('DATE');
      }
    },
    getDates() {
      if (this.value !== null) {
        return [format(this.value[0], 'yyyy-MM-dd'), format(this.value[1], 'yyyy-MM-dd')];
      }
      return [];
    },
    getTimes() {
      if (this.value !== null) {
        return [format(this.value[0], 'HH:mm:ss'), format(this.value[1], 'HH:mm:ss')];
      }
      return ['00:00:00', '00:00:00'];
    },
    onClear() {
      this.emit(null);
      this.$emit('click:clear');
    },
    setBeginTime(time) {
      this.times.splice(0, 1, time);
    },
    updateBeginTime(time) {
      this.times.splice(0, 1, time);
    },
    setEndTime(time) {
      this.times.splice(1, 1, time);
    },
    updateEndTime(time) {
      this.times.splice(1, 1, time);
    },
  },
};
</script>

<style lang="scss" scoped>
.v-picker ::v-deep .v-picker__actions {
  padding: 0;
}
.input-lg {
  width: 285px;
}
.input-xlg {
  width: 300px;
}
.time-area {
  padding: 9px 5px 9px 5px;
  max-width: 290px;
}
.print-section {
  margin: 0 0 5px 10px;
  width: 80px;
}
.time-section:not(:last-of-type) {
  margin-bottom: 8px;
}
.time-select {
  margin: 0 3px 0 3px;
}
.v-btn-toggle {
  border-radius: 0 0 4px 4px;
}
.v-btn:not(.v-btn--round).v-size--small {
  min-width: 58px;
}
.icon {
  padding: 0 !important;
}
</style>
